// Sprint 6 (Phase 10) — Real Settings page.
//
// Single source of truth for the founder's global preferences:
//   • Show developer details — flips raw-tool reveal across the chat surface.
//   • Telegram notifications — on/off + per-event toggles.
//   • Sound on agent finished — only plays when the tab is hidden (no spam
//     while the founder is actively watching the dashboard).
//
// Hydration is owned by `useSettingsStore.hydrate()` (already wired in
// HomePage). Each toggle PATCHes /api/settings via the store's debounced
// scheduler — no spinner, no "Save" button, just optimistic UX.

import { Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useSettingsStore } from '../stores/settings.store';
import {
  pushSupport,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestNotification,
} from '../lib/push';
import type { PermissionMode } from '../types';
import {
  fetchAllowlistRules,
  deleteAllowlistRule,
  api,
  ApiHttpError,
  type AllowlistRule,
} from '../lib/api';
import { useServersStore } from '../stores/servers.store';
import { Toggle } from '../components/atoms/Toggle';
import { RadioOption } from '../components/atoms/RadioOption';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Icon } from '../components/atoms/Icon';
import { Badge } from '../components/atoms/Badge';
import { NewProjectModal } from '../components/NewProjectModal';
import { RunnersTable } from '../components/settings/RunnersTable';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';

export function SettingsPage() {
  const { t, locale, setLocale } = useTranslation();
  const developerMode = useSettingsStore((s) => s.developerMode);
  const setDeveloperMode = useSettingsStore((s) => s.setDeveloperMode);
  const telegramEnabled = useSettingsStore((s) => s.telegramEnabled);
  const setTelegramEnabled = useSettingsStore((s) => s.setTelegramEnabled);
  const soundOnFinished = useSettingsStore((s) => s.soundOnFinished);
  const setSoundOnFinished = useSettingsStore((s) => s.setSoundOnFinished);
  const notificationTiers = useSettingsStore((s) => s.notificationTiers);
  const setNotificationTiers = useSettingsStore((s) => s.setNotificationTiers);
  const defaultPermissionMode = useSettingsStore((s) => s.defaultPermissionMode);
  const setDefaultPermissionMode = useSettingsStore((s) => s.setDefaultPermissionMode);
  const hydrate = useSettingsStore((s) => s.hydrate);
  const hydrated = useSettingsStore((s) => s.hydrated);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  return (
    <main
      className="flex flex-col h-full w-full bg-background overflow-y-auto"
      data-testid="settings-page"
    >
      <header className="px-4 md:px-6 h-14 border-b border-border bg-background flex items-center justify-between shrink-0 pt-safe-top box-content">
        <h1 className="text-body font-semibold text-foreground">{t('settings.heading')}</h1>
        <Link
          to="/"
          className="text-body text-muted-foreground hover:text-foreground"
          data-testid="settings-back"
        >
          {t('settings.backToChat')}
        </Link>
      </header>

      <div className="flex-1 px-4 md:px-6 py-6">
        <div
          className="max-w-2xl mx-auto space-y-8"
          data-testid="settings-content"
          aria-busy={!hydrated}
        >
          <Section
            title={t('settings.display.title')}
            description={t('settings.display.description')}
          >
            <ToggleRow
              label={t('settings.display.developerMode')}
              description={t('settings.display.developerModeDescription')}
              checked={developerMode}
              onChange={setDeveloperMode}
              testId="toggle-developer-mode"
            />
          </Section>

          <Section
            title={t('settings.notifications.title')}
            description={t('settings.notifications.description')}
          >
            {/* Sprint notification-center (spec D3) — one tier matrix,
                not N toggles. The per-event Telegram checkboxes are gone
                (they were dead); each tier row shows what it does and
                exposes only its genuinely configurable switch. */}
            <div
              className="rounded-md border border-border/50 bg-card divide-y divide-border/50"
              data-testid="notification-tier-matrix"
            >
              <div className="flex items-start gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-body text-foreground leading-tight">
                    {t('settings.notifications.tierA')}
                  </div>
                  <div className="text-caption text-muted-foreground mt-1 leading-snug">
                    {t('settings.notifications.tierADescription')}
                  </div>
                </div>
                <Toggle
                  checked={notificationTiers.actionRequiredPush}
                  onCheckedChange={(v) =>
                    setNotificationTiers({ ...notificationTiers, actionRequiredPush: v })
                  }
                  data-testid="toggle-tier-a-push"
                />
              </div>
              <div className="flex items-start gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-body text-foreground leading-tight">
                    {t('settings.notifications.tierB')}
                  </div>
                  <div className="text-caption text-muted-foreground mt-1 leading-snug">
                    {t('settings.notifications.tierBDescription')}
                  </div>
                </div>
                <Toggle
                  checked={notificationTiers.informationalFeed}
                  onCheckedChange={(v) =>
                    setNotificationTiers({ ...notificationTiers, informationalFeed: v })
                  }
                  data-testid="toggle-tier-b-feed"
                />
              </div>
              <div className="flex items-start gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-body text-foreground leading-tight">
                    {t('settings.notifications.tierC')}
                  </div>
                  <div className="text-caption text-muted-foreground mt-1 leading-snug">
                    {t('settings.notifications.tierCDescription')}
                  </div>
                </div>
              </div>
            </div>
            <ToggleRow
              label={t('settings.notifications.sound')}
              description={t('settings.notifications.soundDescription')}
              checked={soundOnFinished}
              onChange={setSoundOnFinished}
              testId="toggle-sound-on-finished"
            />
            <PushDeviceRow />
            <ToggleRow
              label={t('settings.notifications.telegram')}
              description={t('settings.notifications.telegramDescription')}
              checked={telegramEnabled}
              onChange={setTelegramEnabled}
              testId="toggle-telegram-enabled"
            />
          </Section>

          <Section
            title={t('settings.permissions.title')}
            description={t('settings.permissions.description')}
          >
            <div className="space-y-2" data-testid="permission-mode-selector">
              {/*
                Sprint mode-trust-mode-truth-up (2026-06-04): 'auto' was
                removed from this picker. It is now a runtime alias of
                'agent' (same CLI flag + same hook); the legacy 'auto'
                value still validates server-side, and the settings store
                coerces a stored 'auto' to 'agent' on hydrate so the
                radio aligns with what is actually picked.
              */}
              {([
                { value: 'agent' as PermissionMode, labelKey: 'modeChip.agent' as const, descKey: 'modeChip.agentDesc' as const },
                { value: 'plan' as PermissionMode, labelKey: 'modeChip.plan' as const, descKey: 'modeChip.planDesc' as const },
              ] as const).map((opt) => (
                <div
                  key={opt.value}
                  className="flex items-start gap-3 rounded-md p-3 hover:bg-secondary/60 border border-border/50 bg-card cursor-pointer"
                  data-testid={`permission-mode-${opt.value}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setDefaultPermissionMode(opt.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDefaultPermissionMode(opt.value); } }}
                >
                  <RadioOption
                    checked={defaultPermissionMode === opt.value}
                    onCheckedChange={() => setDefaultPermissionMode(opt.value)}
                    label=""
                    name="defaultPermissionMode"
                  />
                  <div className="flex-1 min-w-0 -mt-0.5">
                    <div className="text-body text-foreground leading-tight">{t(opt.labelKey)}</div>
                    <div className="text-caption text-muted-foreground mt-0.5 leading-snug">{t(opt.descKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section
            title={t('settings.language.title')}
            description={t('settings.language.description')}
          >
            <div className="flex items-center gap-6 rounded-md p-3 border border-border/50 bg-card">
              <RadioOption
                checked={locale === 'en'}
                onCheckedChange={() => setLocale('en' as Locale)}
                label={t('settings.language.english')}
                name="language"
              />
              <RadioOption
                checked={locale === 'es'}
                onCheckedChange={() => setLocale('es' as Locale)}
                label={t('settings.language.spanish')}
                name="language"
              />
            </div>
          </Section>

          <ServersSection />

          <ProjectsSection />

          <RunnersSection />

          <AllowlistSection />
        </div>
      </div>
    </main>
  );
}

// Sprint notification-center (spec D2/D7) — per-device Web Push opt-in.
// iOS requires the installed PWA + a user gesture for the permission
// prompt, so this is a button, not a toggle that flips on hydrate. The
// row reflects the real subscription state of THIS device.
function PushDeviceRow() {
  const { t } = useTranslation();
  const [state, setState] = useState<'loading' | 'unsupported' | 'denied' | 'off' | 'on' | 'busy'>(
    'loading',
  );
  // Sprint test-notification-button (spec D3) — the "Send test notification"
  // control lives here so it shares the live subscription state: it can only
  // fire when THIS device is subscribed, which also implies WEB_PUSH_ENABLED
  // (you cannot subscribe when push is off). A tap can never silently no-op.
  const [testState, setTestState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const support = pushSupport();
    if (support === 'unsupported') {
      setState('unsupported');
      return;
    }
    if (support === 'denied') {
      setState('denied');
      return;
    }
    const sub = await getCurrentSubscription();
    setState(sub ? 'on' : 'off');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleClick() {
    const enabling = state === 'off';
    setState('busy');
    const ok = enabling ? await subscribeToPush() : await unsubscribeFromPush();
    if (!ok && enabling && Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    // A change of subscription state invalidates any prior test result.
    setTestState('idle');
    setTestMessage(null);
    await refresh();
  }

  async function handleSendTest() {
    setTestState('sending');
    setTestMessage(null);
    try {
      await sendTestNotification();
      setTestState('sent');
      setTestMessage(t('settings.notifications.testSent'));
    } catch (err) {
      setTestState('error');
      setTestMessage(
        err instanceof ApiHttpError && err.code === 'RATE_LIMITED'
          ? t('settings.notifications.testRateLimited')
          : t('settings.notifications.testFailed'),
      );
    }
  }

  const testDisabled = state !== 'on' || testState === 'sending';

  return (
    <div
      className="rounded-md border border-border/50 bg-card"
      data-testid="push-device-row"
    >
      <div className="flex items-start gap-3 p-3">
        <div className="flex-1 min-w-0">
          <div className="text-body text-foreground leading-tight">
            {t('settings.notifications.pushDevice')}
          </div>
          <div className="text-caption text-muted-foreground mt-1 leading-snug">
            {state === 'unsupported'
              ? t('settings.notifications.pushUnsupported')
              : state === 'denied'
                ? t('settings.notifications.pushDenied')
                : t('settings.notifications.pushDeviceDescription')}
          </div>
        </div>
        {(state === 'off' || state === 'on' || state === 'busy') && (
          <Button
            variant="outline"
            size="sm"
            disabled={state === 'busy'}
            onClick={() => void handleClick()}
            className="shrink-0 min-h-touch-target"
            data-testid="push-device-button"
          >
            {state === 'on'
              ? t('settings.notifications.pushDisable')
              : t('settings.notifications.pushEnable')}
          </Button>
        )}
      </div>
      {/* Sprint test-notification-button (spec D3) — disabled with a hint
          until this device is subscribed; ≥44px touch target (mobile-first). */}
      <div className="flex items-center gap-3 px-3 pb-3">
        <Button
          variant="outline"
          size="sm"
          disabled={testDisabled}
          onClick={() => void handleSendTest()}
          className="shrink-0 min-h-touch-target"
          data-testid="test-notification-button"
        >
          {testState === 'sending'
            ? t('settings.notifications.testSending')
            : t('settings.notifications.testSend')}
        </Button>
        {testMessage ? (
          <span
            role="status"
            className={`text-caption leading-snug ${
              testState === 'error' ? 'text-status-error' : 'text-muted-foreground'
            }`}
            data-testid="test-notification-status"
          >
            {testMessage}
          </span>
        ) : (
          state !== 'on' && (
            <span
              className="text-caption text-muted-foreground leading-snug"
              data-testid="test-notification-hint"
            >
              {t('settings.notifications.testDisabledHint')}
            </span>
          )
        )}
      </div>
    </div>
  );
}

// S6.5 Phase 6 — "Allowed forever" panel.
//
// Lists every rule in permission_allowlist and lets the founder revoke any
// of them with one tap. Rules are GLOBAL (D2): one rule applies to every
// agent. The list is sorted newest-first so the rule the founder added 5
// seconds ago is right at the top when he clicks "Forget it" because he
// realised he scoped it too widely.
//
// Refetch happens on mount + on tab focus + after a successful delete. No
// websocket push for now — the list is short (typically <50 rows), the
// Settings page is rarely open, and stale-by-tab-focus matches the
// expected mental model.
const ALLOWLIST_VISIBLE_CAP = 10;

function AllowlistSection() {
  const { t } = useTranslation();
  const [rules, setRules] = useState<AllowlistRule[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await fetchAllowlistRules();
      next.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setRules(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.allowlist.couldNotLoad'));
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reload]);

  async function handleForget(id: string) {
    setDeletingId(id);
    setError(null);
    const previous = rules ?? [];
    setRules(previous.filter((r) => r.id !== id));
    try {
      await deleteAllowlistRule(id);
    } catch (err) {
      setRules(previous);
      setError(err instanceof Error ? err.message : t('settings.allowlist.couldNotForget'));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Section
      title={t('settings.allowlist.title')}
      description={t('settings.allowlist.description')}
    >
      {error && (
        <p
          role="alert"
          className="text-caption text-status-error"
          data-testid="allowlist-error"
        >
          {error}
        </p>
      )}

      {rules === null && loading ? (
        <p
          className="text-caption text-muted-foreground"
          data-testid="allowlist-loading"
        >
          {t('settings.allowlist.loading')}
        </p>
      ) : rules && rules.length === 0 ? (
        <p
          className="text-caption text-muted-foreground rounded-md border border-border/50 bg-card p-3"
          data-testid="allowlist-empty"
        >
          {t('settings.allowlist.empty')}
        </p>
      ) : (
        <>
          <ul
            className="space-y-2"
            data-testid="allowlist-list"
            aria-busy={loading}
          >
            {(expanded ? rules ?? [] : (rules ?? []).slice(0, ALLOWLIST_VISIBLE_CAP)).map((rule) => (
              <AllowlistRow
                key={rule.id}
                rule={rule}
                onForget={() => void handleForget(rule.id)}
                busy={deletingId === rule.id}
              />
            ))}
          </ul>
          {!expanded && (rules ?? []).length > ALLOWLIST_VISIBLE_CAP && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="w-full mt-2 py-2 rounded-md text-body text-muted-foreground hover:text-foreground hover:bg-secondary/60 border border-border/50 bg-card transition-colors"
              data-testid="allowlist-show-all"
            >
              {t('settings.allowlist.showAll', { count: (rules ?? []).length })}
            </button>
          )}
        </>
      )}
    </Section>
  );
}

function AllowlistRow({
  rule,
  onForget,
  busy,
}: {
  rule: AllowlistRule;
  onForget: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const { kindLabel, scopeLabel } = humanizeRule(rule, t);
  return (
    <li
      className="flex items-start gap-3 rounded-md border border-border/50 bg-card p-3"
      data-testid="allowlist-row"
      data-rule-id={rule.id}
    >
      <div className="flex-1 min-w-0">
        <div className="text-label uppercase text-muted-foreground">
          {kindLabel}
        </div>
        <div
          className="text-body text-foreground font-mono break-all leading-snug mt-0.5"
          data-testid="allowlist-row-scope"
        >
          {scopeLabel}
        </div>
      </div>
      <button
        type="button"
        onClick={onForget}
        disabled={busy}
        className="shrink-0 px-3 py-2 rounded-md text-caption text-status-error border border-status-error/40 hover:bg-status-error/10 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-status-error/30"
        style={{ minHeight: 44, minWidth: 44 }}
        data-testid="allowlist-forget"
      >
        {busy ? t('settings.allowlist.forgetting') : t('settings.allowlist.forget')}
      </button>
    </li>
  );
}

// S6.5 Phase 6 — render an allowlist row in plain English. The pattern
// stored on disk for Bash is the raw command, for file ops the path, for
// Glob/Grep the pattern, for unknown tools a JSON blob (capped 4 KiB). We
// keep it simple here: a tool kind label + the pattern verbatim, truncated
// for absurd cases. The founder always has the full string in the title
// attribute on hover.
type TranslateFn = (key: Parameters<ReturnType<typeof useTranslation>['t']>[0]) => string;

const TOOL_KIND_KEYS: Record<string, Parameters<TranslateFn>[0]> = {
  Bash: 'settings.allowlist.toolKind.Bash',
  Read: 'settings.allowlist.toolKind.Read',
  Edit: 'settings.allowlist.toolKind.Edit',
  Write: 'settings.allowlist.toolKind.Write',
  MultiEdit: 'settings.allowlist.toolKind.MultiEdit',
  NotebookEdit: 'settings.allowlist.toolKind.NotebookEdit',
  Glob: 'settings.allowlist.toolKind.Glob',
  Grep: 'settings.allowlist.toolKind.Grep',
  WebFetch: 'settings.allowlist.toolKind.WebFetch',
};

function humanizeRule(
  rule: AllowlistRule,
  t: TranslateFn,
): {
  kindLabel: string;
  scopeLabel: string;
} {
  const key = TOOL_KIND_KEYS[rule.toolName];
  const kindLabel = key ? t(key) : rule.toolName;
  const raw = rule.pattern ?? '';
  const scopeLabel = raw.length > 200 ? raw.slice(0, 199) + '…' : raw;
  return { kindLabel, scopeLabel };
}

// Sprint 11b — Servers management section.
function ServersSection() {
  const { t } = useTranslation();
  const servers = useServersStore((s) => s.servers);
  const addServer = useServersStore((s) => s.addServer);
  const removeServer = useServersStore((s) => s.removeServer);
  const probeServer = useServersStore((s) => s.probeServer);

  const [adding, setAdding] = useState(false);
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');

  function handleAdd() {
    if (!newId.trim() || !newName.trim() || !newUrl.trim()) return;
    addServer({ id: newId.trim(), name: newName.trim(), baseUrl: newUrl.trim() });
    void probeServer(newId.trim());
    setAdding(false);
    setNewId('');
    setNewName('');
    setNewUrl('');
  }

  return (
    <Section
      title={t('settings.servers.title')}
      description={t('settings.servers.description')}
    >
      {servers.length === 0 && !adding ? (
        <p
          className="text-caption text-muted-foreground rounded-md border border-border/50 bg-card p-3"
          data-testid="servers-empty"
        >
          {t('settings.servers.empty')}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="servers-list">
          {servers.map((server) => (
            <li
              key={server.id}
              className="flex items-center gap-3 rounded-md border border-border/50 bg-card p-3"
              data-testid={`server-row-${server.id}`}
            >
              <Badge
                variant="dot"
                color={server.status === 'online' ? 'success' : server.status === 'offline' ? 'error' : 'warning'}
                className="w-2.5 h-2.5 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-body text-foreground font-550 truncate">{server.name}</div>
                <div className="text-caption text-muted-foreground truncate">{server.baseUrl}</div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void probeServer(server.id)}
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label={t('common.refresh')}
              >
                <Icon name="RefreshCw" size={14} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeServer(server.id)}
                className="h-8 w-8 text-status-error hover:text-status-error/80"
                aria-label={t('settings.servers.remove')}
              >
                <Icon name="Trash2" size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="space-y-2 rounded-md border border-border/50 bg-card p-3" data-testid="add-server-form">
          <Input
            placeholder={t('settings.servers.idPlaceholder')}
            value={newId}
            onChange={(e) => setNewId(e.target.value)}
            className="h-8 text-sm"
            data-testid="add-server-id"
          />
          <Input
            placeholder={t('settings.servers.namePlaceholder')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="h-8 text-sm"
            data-testid="add-server-name"
          />
          <Input
            placeholder={t('settings.servers.baseUrlPlaceholder')}
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className="h-8 text-sm"
            data-testid="add-server-url"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              {t('settings.servers.cancel')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleAdd}
              disabled={!newId.trim() || !newName.trim() || !newUrl.trim()}
            >
              {t('settings.servers.save')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAdding(true)}
          className="gap-1.5"
          data-testid="add-server-button"
        >
          <Icon name="Plus" size={14} />
          {t('settings.servers.addServer')}
        </Button>
      )}
    </Section>
  );
}

// Sprint 11b — Projects management section.
interface ProjectEntry {
  id: string;
  name: string;
  workdir: string;
  defaultBranch: string;
  archivedAt: string | null;
}

function ProjectsSection() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<ProjectEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [newModalOpen, setNewModalOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api<{ projects: ProjectEntry[] }>('/api/projects?archived=true');
      setProjects(res.projects);
    } catch {
      // Non-critical.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleArchive(id: string) {
    try {
      await api(`/api/projects/${encodeURIComponent(id)}/archive`, { method: 'POST' });
      void reload();
    } catch {
      // Non-critical.
    }
  }

  async function handleUnarchive(id: string) {
    try {
      await api(`/api/projects/${encodeURIComponent(id)}/unarchive`, { method: 'POST' });
      void reload();
    } catch {
      // Non-critical.
    }
  }

  return (
    <Section
      title={t('settings.projects.title')}
      description={t('settings.projects.description')}
    >
      <div className="flex justify-end mb-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setNewModalOpen(true)}
          data-testid="settings-add-project"
        >
          <Icon name="Plus" size={14} className="mr-1" aria-hidden="true" />
          {t('workspace.addProject')}
        </Button>
      </div>
      <NewProjectModal
        open={newModalOpen}
        onOpenChange={setNewModalOpen}
        onCreated={() => {
          void reload();
        }}
      />
      {projects.length === 0 ? (
        <p
          className="text-caption text-muted-foreground rounded-md border border-border/50 bg-card p-3"
          data-testid="projects-empty"
          aria-busy={loading}
        >
          {t('settings.projects.empty')}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="projects-list" aria-busy={loading}>
          {projects.map((p) => (
            <li
              key={p.id}
              className={`flex items-center gap-3 rounded-md border border-border/50 bg-card p-3 ${
                p.archivedAt ? 'opacity-60' : ''
              }`}
              data-testid={`project-row-${p.id}`}
            >
              <Icon name="FolderOpen" size={16} className="shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="text-body text-foreground font-550 truncate">{p.name}</div>
                <div className="text-caption text-muted-foreground truncate">
                  {p.workdir}
                  {p.defaultBranch !== 'main' && ` · ${p.defaultBranch}`}
                </div>
              </div>
              {p.id !== 'cw-crm-v2' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void (p.archivedAt ? handleUnarchive(p.id) : handleArchive(p.id))}
                  className="text-caption"
                >
                  {p.archivedAt ? t('settings.projects.unarchive') : t('settings.projects.archive')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// Sprint 20.3 PR-8 (§H.1) — admin runners panel section. Renders the
// `RunnersTable` component which owns its own data fetching, polling,
// and per-row drain/undrain/clear-unavailable actions. The section
// itself is auth-gated server-side (`requireAuth` on `/api/runners`);
// per-user admin ACL lands in S20.4 (single-user dashboard today, so
// the section is rendered unconditionally).
function RunnersSection() {
  const { t } = useTranslation();
  return (
    <Section
      title={t('settings.runners.title')}
      description={t('settings.runners.description')}
    >
      <RunnersTable />
    </Section>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4" data-testid={`settings-section-${title.toLowerCase()}`}>
      <header>
        <h2 className="text-label text-muted-foreground uppercase">
          {title}
        </h2>
        <p className="text-caption text-muted-foreground mt-0.5">{description}</p>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  testId,
  compact,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  testId?: string;
  compact?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-3 cursor-pointer group ${
        compact ? '' : 'rounded-md p-3 hover:bg-secondary/60 border border-border/50 bg-card'
      }`}
      data-testid={testId}
    >
      <div className="flex-1 min-w-0">
        <div className="text-body text-foreground leading-tight">{label}</div>
        {description && (
          <div className="text-caption text-muted-foreground mt-1 leading-snug">{description}</div>
        )}
      </div>
      <Toggle checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

